import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { PlayerVerticalSliceView } from '@aztec-realms/shared-types';

import type { BuildSettlement } from './settlement-build-api';
import {
  loadPlacementLayout,
  placementErrorMessage,
  placementFootprint,
  placementMatches,
  placementProblem,
  savePlacement,
  type PlacementDraft,
} from './settlement-placement';
import { verticalSliceQueryKey } from './use-vertical-slice';

interface PlacementSession {
  layout: BuildSettlement;
  draft: PlacementDraft;
}

export function useSettlementPlacement(locked: boolean) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<PlacementSession>();
  const [activeId, setActiveId] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const sequence = useRef(0);
  const lockedRef = useRef(locked);
  useLayoutEffect(() => {
    lockedRef.current = locked;
  }, [locked]);
  const busyRef = useRef(false);

  const cancel = useCallback(() => {
    sequence.current += 1;
    busyRef.current = false;
    setSession(undefined);
    setActiveId(undefined);
    setBusy(false);
    setError(undefined);
  }, []);

  useEffect(
    () => () => {
      sequence.current += 1;
    },
    [],
  );
  useEffect(() => {
    if (locked) {
      // The lock ref and preview gate act immediately; discard the draft after
      // this render rather than synchronously scheduling a cascading render.
      queueMicrotask(() => {
        cancel();
        setNotice('Building changes paused while you are moving too fast.');
      });
    }
  }, [locked, cancel]);

  const begin = useCallback(
    async (buildingId: string, retainedDraft?: PlacementDraft) => {
      if (lockedRef.current || busyRef.current || !navigator.onLine) return;
      const operation = ++sequence.current;
      busyRef.current = true;
      setBusy(true);
      setActiveId(buildingId);
      setError(undefined);
      setNotice(undefined);
      try {
        const layout = await loadPlacementLayout();
        if (operation !== sequence.current || lockedRef.current) return;
        const building = layout.buildings.find((item) => item.id === buildingId);
        if (!building) throw new Error('This building is no longer available.');
        const draft = retainedDraft ?? {
          buildingId,
          x: building.x,
          y: building.y,
          districtCode: building.district_code,
          rotation: building.rotation,
        };
        setSession({ layout, draft });
        if (retainedDraft && placementMatches(layout, draft))
          setNotice('This position is already saved.');
        void queryClient.invalidateQueries({ queryKey: verticalSliceQueryKey });
      } catch {
        if (operation === sequence.current)
          setError('Your city could not load. Reconnect and try again.');
      } finally {
        if (operation === sequence.current) {
          busyRef.current = false;
          setBusy(false);
        }
      }
    },
    [queryClient],
  );

  const move = useCallback((x: number, y: number) => {
    if (lockedRef.current || busyRef.current) return;
    setNotice(undefined);
    setSession((current) =>
      current ? { ...current, draft: { ...current.draft, x, y } } : current,
    );
  }, []);

  const rotate = useCallback((direction: 1 | -1) => {
    if (lockedRef.current || busyRef.current) return;
    setNotice(undefined);
    setSession((current) =>
      current
        ? {
            ...current,
            draft: { ...current.draft, rotation: (current.draft.rotation + direction + 4) % 4 },
          }
        : current,
    );
  }, []);

  const save = async () => {
    if (
      !session ||
      !!error ||
      lockedRef.current ||
      busyRef.current ||
      !navigator.onLine ||
      placementMatches(session.layout, session.draft) ||
      placementProblem(session.layout, session.draft)
    )
      return;
    const operation = ++sequence.current;
    busyRef.current = true;
    setBusy(true);
    setError(undefined);
    try {
      const building = await savePlacement(session.layout, session.draft);
      // Only a server response updates the durable player view; never the drag preview.
      queryClient.setQueryData<PlayerVerticalSliceView>(verticalSliceQueryKey, (current) =>
        current
          ? {
              ...current,
              settlement: {
                ...current.settlement,
                buildings: current.settlement.buildings.map((item) =>
                  item.id === building.id
                    ? {
                        ...item,
                        gridX: building.x,
                        gridY: building.y,
                        footprintWidth: building.footprint_width,
                        footprintHeight: building.footprint_height,
                        orientation: building.rotation,
                        districtCode: building.district_code,
                      }
                    : item,
                ),
              },
            }
          : current,
      );
      void queryClient.invalidateQueries({ queryKey: verticalSliceQueryKey });
      void queryClient.invalidateQueries({ queryKey: ['player', 'building-catalog'] });
      if (operation === sequence.current) {
        cancel();
        setNotice('Building position saved.');
      }
    } catch (failure) {
      if (operation === sequence.current) setError(placementErrorMessage(failure));
    } finally {
      if (operation === sequence.current) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };

  const district = session?.layout.districts.find(
    (item) => item.code === session.draft.districtCode,
  );
  const selected = session?.layout.buildings.find((item) => item.id === session.draft.buildingId);
  const footprint =
    selected && session ? placementFootprint(selected, session.draft.rotation) : undefined;
  return {
    active: !!activeId && !locked,
    activeId: locked ? undefined : activeId,
    session,
    busy,
    error,
    notice,
    begin,
    cancel,
    move,
    rotate,
    save,
    refresh: () => activeId && begin(activeId, session?.draft),
    changeDistrict: (code: string) => {
      if (busyRef.current || lockedRef.current) return;
      setSession((current) => {
        const target = current?.layout.districts.find(
          (item) => item.code === code && item.unlocked_at,
        );
        return current && target
          ? {
              ...current,
              draft: { ...current.draft, districtCode: code, x: target.min_x, y: target.min_y },
            }
          : current;
      });
    },
    problem: session ? placementProblem(session.layout, session.draft) : undefined,
    unchanged: session ? placementMatches(session.layout, session.draft) : true,
    preview:
      session && !locked
        ? {
            buildingId: session.draft.buildingId,
            x: session.draft.x,
            y: session.draft.y,
            orientation: session.draft.rotation,
            ...footprint,
            valid: !placementProblem(session.layout, session.draft),
            districtBounds: district
              ? {
                  minX: district.min_x,
                  maxX: district.max_x + 1,
                  minY: district.min_y,
                  maxY: district.max_y + 1,
                }
              : undefined,
          }
        : undefined,
  };
}
